import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBell, FaCalendarDay, FaClock, FaExclamationTriangle, FaTasks } from 'react-icons/fa';
import { PiBellSimpleSlashDuotone } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_CORE } from '../../../../../../_CORE/API/ApiCORE';
import { LAYOUTS } from '../../../../../../_CORE/_RuntimeConfig/_Config/_Layouts.js';
import { APP_COMPANY } from '../../../../../../_CORE/_RuntimeConfig/_Config/Acomodar/_Company.js';
import { useLanguage } from '../../../../../../_CORE/Contexts/Translate/useLanguageContext';
import { useHeaderUtilities } from '../_Hooks/useHeaderUtilities';
import { UtilityDropdown, UtilityItem, UtilityEmpty, UTILITY_MAX_ITEMS } from '../UtilityDropdown';

const NOTIFICATION_CONFIG = LAYOUTS.SHELL.NOTIFICATIONS;
const REFRESH_MS = NOTIFICATION_CONFIG.REFRESH_MS;
const MAX_READ_IDS = NOTIFICATION_CONFIG.MAX_READ_IDS;
const NOTIFICATION_ENDPOINT = NOTIFICATION_CONFIG.ENDPOINT;
const NOTIFICATION_STORAGE_PREFIX = NOTIFICATION_CONFIG.STORAGE_PREFIX;
const COMPANY_LOCALE = String(APP_COMPANY.COMPANY_LOCALE || "es_AR").replace("_", "-");

const unwrap = (response) => response?.data?.data ?? response?.data ?? response ?? null;
const succeeded = (response) => response?.success !== false && Number(response?.status ?? response?.code_status ?? 200) < 400;
const asArray = (value) => Array.isArray(value) ? value : [];

const KIND_LABEL = {
    reminder: 'Recordatorio',
    'trello-card': 'Trello',
    todo: 'Tarea',
    'course-task': 'Curso',
};

const BUCKET_LABEL = {
    overdue: 'Vencido',
    today: 'Para hoy',
    upcoming: 'Próximo',
};

const BUCKET_ORDER = { overdue: 0, today: 1, upcoming: 2 };

const parseLocalDate = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const dateOnly = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatWhen = (value) => {
    const date = parseLocalDate(value);
    if (!date) return '';
    const raw = String(value || '');
    const hasTime = /[T\s]\d{2}:\d{2}/.test(raw);
    return new Intl.DateTimeFormat(COMPANY_LOCALE, {
        day: '2-digit', month: 'short', year: 'numeric',
        ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(date);
};

const notificationIcon = (item) => {
    if (item.bucket === 'overdue') return <FaExclamationTriangle size={14} />;
    if (item.kind === 'reminder') return <FaBell size={14} />;
    if (item.kind === 'trello-card' || item.kind === 'todo' || item.kind === 'course-task') return <FaTasks size={14} />;
    if (item.bucket === 'today') return <FaCalendarDay size={14} />;
    return <FaClock size={14} />;
};

const makeNotification = (item, bucket) => {
    const kind = String(item?.kind || 'item');
    const id = String(item?.id || item?.title || 'unknown');
    const when = String(item?.when || '');
    const label = KIND_LABEL[kind] || 'Pendiente';
    const bucketLabel = BUCKET_LABEL[bucket] || 'Aviso';
    const whenLabel = formatWhen(when);

    return {
        ...item,
        notificationId: `${bucket}:${kind}:${id}:${when}`,
        bucket,
        title: `${bucketLabel} · ${String(item?.title || label)}`,
        description: `${label}${whenLabel ? ` · ${whenLabel}` : ''}`,
        link: kind === 'reminder'
            ? '/reminders'
            : (kind === 'trello-card' || kind === 'todo' || kind === 'course-task')
                ? (item?.url || '/today')
                : '/today',
    };
};

const readStoredIds = (key) => {
    if (typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed.filter(Boolean).slice(-MAX_READ_IDS) : [];
    } catch (_) {
        return [];
    }
};

export const Notifications = ({ user = null, viewAllPath = '/today' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { useDataLanguage } = useLanguage();
    const { onClose } = useHeaderUtilities();

    const TEXT = useDataLanguage.translations?.TEMPLATES?.header ?? {};
    const storageKey = useMemo(() => `${NOTIFICATION_STORAGE_PREFIX}${String(user?.id || 'local')}`, [user?.id]);

    const [source, setSource] = useState({ overdue: [], today: [], upcoming: [] });
    const [readIds, setReadIds] = useState(() => readStoredIds(storageKey));

    useEffect(() => {
        setReadIds(readStoredIds(storageKey));
    }, [storageKey]);

    const load = useCallback(async () => {
        if (!NOTIFICATION_ENDPOINT) return;
        const response = await API_CORE.SERVICES.GET_RESOURCES(NOTIFICATION_ENDPOINT, {});
        if (!succeeded(response)) return;
        const payload = unwrap(response) || {};
        setSource({
            overdue: asArray(payload.overdue),
            today: asArray(payload.today),
            upcoming: asArray(payload.upcoming),
        });
    }, []);

    useEffect(() => {
        load();
        const timer = window.setInterval(load, REFRESH_MS);
        const onFocus = () => load();
        const onVisibility = () => { if (document.visibilityState === 'visible') load(); };
        const onRefresh = () => load();

        window.addEventListener('focus', onFocus);
        window.addEventListener('scool-data:notifications-refresh', onRefresh);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('scool-data:notifications-refresh', onRefresh);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [load]);

    useEffect(() => {
        load();
    }, [location.pathname, load]);

    const items = useMemo(() => {
        const all = [
            ...source.overdue.map((item) => makeNotification(item, 'overdue')),
            ...source.today.map((item) => makeNotification(item, 'today')),
            ...source.upcoming.map((item) => makeNotification(item, 'upcoming')),
        ];
        return all.sort((a, b) => {
            const bucketDiff = (BUCKET_ORDER[a.bucket] ?? 9) - (BUCKET_ORDER[b.bucket] ?? 9);
            if (bucketDiff !== 0) return bucketDiff;
            const aTime = parseLocalDate(a.when)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            const bTime = parseLocalDate(b.when)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
        });
    }, [source]);

    const readSet = useMemo(() => new Set(readIds), [readIds]);
    const unreadItems = items.filter((item) => !readSet.has(item.notificationId));
    const unread = unreadItems.length;
    const visible = unreadItems.slice(0, UTILITY_MAX_ITEMS);

    const persistRead = useCallback((ids) => {
        const unique = Array.from(new Set(ids)).slice(-MAX_READ_IDS);
        setReadIds(unique);
        try { window.localStorage.setItem(storageKey, JSON.stringify(unique)); } catch (_) { /* storage opcional */ }
    }, [storageKey]);

    const markRead = (id) => {
        if (!id || readSet.has(id)) return;
        persistRead([...readIds, id]);
    };

    const onGoTo = (item) => {
        if (!item) return;
        markRead(item.notificationId);
        onClose();

        const target = String(item.link || viewAllPath || '/today').trim();
        if (!target) return;

        navigate(target);
    };

    const onMarkAllRead = () => persistRead([...readIds, ...items.map((item) => item.notificationId)]);
    const onOpenToday = () => { onClose(); navigate(viewAllPath || '/today'); };

    const footer = unreadItems.length > 0 ? (
        <div className="LStudio__app_utility__notification_footer">
            <button type="button" onClick={onOpenToday}>Abrir Hoy</button>
            {unread > 0 && <button type="button" onClick={onMarkAllRead}>Marcar leído</button>}
        </div>
    ) : null;

    return (
        <UtilityDropdown id="notifications" label={TEXT.notifications || 'Notificaciones'} badge={unread}
            width={380} icon={<FaBell size={18} />} footer={footer}>
            {
                visible.length > 0
                    ? visible.map((item) => (
                        <UtilityItem key={item.notificationId} icon={notificationIcon(item)}
                            title={item.title} description={item.description}
                            meta={item.bucket === 'overdue' ? 'Requiere atención' : item.bucket === 'today' ? 'Vence hoy' : 'Próximos 7 días'}
                            isUnread={!readSet.has(item.notificationId)} onClick={() => onGoTo(item)} />
                    ))
                    : <UtilityEmpty icon={<PiBellSimpleSlashDuotone size={44} />}
                        title="Todo al día" hint="Los vencimientos, recordatorios y tareas próximas van a aparecer acá." />
            }
            {unreadItems.length > UTILITY_MAX_ITEMS && (
                <button type="button" className="LStudio__app_utility__notification_more" onClick={onOpenToday}>
                    +{unreadItems.length - UTILITY_MAX_ITEMS} avisos más
                </button>
            )}
        </UtilityDropdown>
    );
};
